import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { LoginComponent } from '@security/login/login.component';
import { LogoutComponent } from '@security/logout/logout.component';

import { AuthGuard } from '@auth0/auth0-angular';
import {
	AccountAdminGuard,
	AdminGuard,
	FeatureGuard
} from '@services/authentication.service';
import { AccountFeatures } from '@services/accounts.service';

import { HomeNavigationComponent } from '@layout/home-navigation/home-navigation.component';
import { HomeDefaultPageComponent } from '@layout/home-default-page/home-default-page.component';

import { ProfileManageComponent } from '@admin/users/profile-manage/profile-manage.component';
import { ProfileComponent } from '@admin/users/profile/profile.component';
import { AccountManageComponent } from '@admin/accounts/account-manage/account-manage.component';
import { AccountInviteComponent } from '@admin/accounts/account-invite/account-invite.component';
import { AccountManageFeaturesComponent } from '@admin/accounts/account-manage-features/account-manage-features.component';

import { SettingsComponent } from '@components/site-settings/settings.component';
import { HelpComponent } from '@components/help/help.component';
import { NotificationsComponent } from '@components/notifications/notifications.component';
import { RoutingComponent } from '@components/routing/routing.component';
import { RedirectComponent } from '@components/redirect/redirect.component';

import { EventsComponent } from '@components/events/events/events.component';
import { EventDetailsComponent } from '@components/events/event-details/event-details.component';

import { RulesComponent } from '@components/rules/rules/rules.component';
import { RuleDetailsComponent } from '@components/rules/rule-details/rule-details.component';

import { ActionListComponent } from '@components/actions/action-list/action-list.component';
import { ActionDetailsComponent } from '@components/actions/action-details/action-details.component';

import { ModelsComponent } from '@components/models/models/models.component';
import { ModelDetailsComponent } from '@components/models/model-details/model-details.component';

import { WatchListComponent } from '@components/watch-list/watch-list.component';
import { UserFavouritesComponent } from '@components/user-favourites/user-favourites.component';

import { AssetListComponent } from './organisation-assets/asset-list/asset-list.component';
import { AssetDetailsComponent } from './organisation-assets/asset-details/asset-details.component';
import { AssetComparisonComponent } from './organisation-assets/asset-comparison/asset-comparison.component';

import { OrganisationFlaggedAssetsComponent } from '@components/organisation-flagged-assets/organisation-flagged-assets.component';

import { SAPCaseListComponent } from './cmms/sap-case-list/sap-case-list.component';
import { SAPCaseDetailComponent } from './cmms/sap-case-detail/sap-case-detail.component';

import { EnergyDashboardComponent } from './organisations/energy-dashboard/energy-dashboard.component';
import { EnergyReportingComponent } from '@components/energy-reporting/energy-reporting.component';
import { HistoricalReportsComponent } from '@components/historical-reports/historical-reports.component';

import { HierarchyDashboardComponent } from './organisations/hierarchy/hierarchy-dashboard/hierarchy-dashboard.component';
import { HierarchyManagementComponent } from './organisations/hierarchy/management/hierarchy-management/hierarchy-management.component';
import { OrganisationDataModelComponent } from './organisations/hierarchy/management/organisation-data-model/organisation-data-model.component';

import { DashboardCreationComponent } from './organisations/dashboard/dashboard-creation/dashboard-creation.component';
import { CustomDashboardComponent } from './organisations/dashboard/custom-dashboard/custom-dashboard.component';

import { DataIntegrationsComponent } from './integrations/data-integrations/data-integrations.component';
import { DataIntegrationsDetailComponent } from './integrations/data-integrations-detail/data-integrations-detail.component';
import { DataIntegrationsNeuronsensorsManageComponent } from './integrations/data-integrations-neuronsensors-manage/data-integrations-neuronsensors-manage.component';

import { VirtualSensorsComponent } from '@components/virtual-sensors/virtual-sensors.component';
import { VirtualSensorDetailsComponent } from '@components/virtual-sensor-details/virtual-sensor-details.component';

import { AccountdisabledComponent } from '@admin/users/accountdisabled/accountdisabled.component';
import { UnverifiedEmailComponent } from '@admin/users/unverified-email/unverified-email.component';
import { UnauthorisedComponent } from '@admin/users/unauthorised/unauthorised.component';
import { ErrorComponent } from './error/error.component';
import { AccountOrganisationGuard } from './guards/account-organisation.guard';
import { maintenanceGuard } from './guards/maintenance.guard';
import { MaintenanceComponent } from '@layout/maintenance/maintenance.component';
import { AppConfigComponent } from '@admin/app-config/app-config.component';
import { ToSGuard } from './guards/ToSGuard.guard';
import { TOSComponent } from '@security/tos/tos.component';
import { PendingChangesGuard } from './guards/PendingChangesGuard.guard';
import { SwitchAccountsComponent } from '@layout/switch-accounts/switch-accounts.component';
import { DataIntegrationsVirtualsensorsManageComponent } from './integrations/data-integrations-virtualsensors-manage/data-integrations-virtualsensors-manage.component';

/** TitlePrefix */
const TitlePrefix = "PRISMPdM"
/**
 * Build the master root pages.
 */
const routes: Routes = [

	/*
	* ============================================================
	* MAINTENANCE
	* ============================================================
	*/
	{
		path: 'maintenance',
		title: `${TitlePrefix} - Maintenance`,
		component: MaintenanceComponent,
	},

	/*
	* --------------------------------------------------------
	* Switch Accounts
	* --------------------------------------------------------
	*/
	{
		path: 'switchaccounts',
		title: `${TitlePrefix} - Switch Accounts`,
		component: SwitchAccountsComponent,
		canActivate: [ToSGuard, AuthGuard],
	},

	/*
	* ============================================================
	* PUBLIC / HOME
	* ============================================================
	*/
	{

		path: '',
		title: `${TitlePrefix} - Home`,
		component: HomeNavigationComponent,
		// canActivate: [maintenanceGuard],
		children: [

			{
				path: '',
				component: HomeDefaultPageComponent
			},

			/*
			* --------------------------------------------------------
			* user-agreement Agreement
			* --------------------------------------------------------
			*/
			{
				path: 'user-agreement',
				title: `${TitlePrefix} - Terms of Service Agreement`,
				component: TOSComponent,
			},

			/*
			* ============================================================
			* AUTHENTICATED APPLICATION
			* ============================================================
			*
			* Everything underneath HomeNavigation requires authentication.
			*/
			{
				path: '',
				title: `${TitlePrefix} - Home`,
				canActivate: [maintenanceGuard],
				canActivateChild: [ToSGuard, AuthGuard],
				children: [

					/*
					* --------------------------------------------------------
					* APP CONFIG
					* --------------------------------------------------------
					*/
					{
						path: 'appconfig',
						title: `${TitlePrefix} - Application Configuration`,
						component: AppConfigComponent,
						canActivate: [AdminGuard],
						canDeactivate: [PendingChangesGuard]
					},

					/*
					* --------------------------------------------------------
					* USER MANAGEMENT
					* --------------------------------------------------------
					*/
					{
						path: 'users-management',
						title: `${TitlePrefix} - User Management`,
						component: ProfileManageComponent,
						canActivate: [AdminGuard],

						children: [
							{
								path: ':userGUID',
								component: ProfileComponent
							}
						]
					},


					/*
					 * --------------------------------------------------------
					 * ACCOUNT MANAGEMENT
					 * --------------------------------------------------------
					 */
					{
						path: 'accounts-management/:accountID',
						title: `${TitlePrefix} - Account Management`,
						component: AccountManageComponent
					},

					{
						path: 'feature-management',
						title: `${TitlePrefix} - Feature Management`,
						component: AccountManageFeaturesComponent,
						canActivate: [AdminGuard]
					},


					/*
					 * --------------------------------------------------------
					 * USER PAGES
					 * --------------------------------------------------------
					 */
					{
						path: 'profile',
						title: `${TitlePrefix} - Profile`,
						component: ProfileComponent
					},

					{
						path: 'settings',
						title: `${TitlePrefix} - Settings`,
						component: SettingsComponent
					},

					{
						path: 'help',
						title: `${TitlePrefix} - Help`,
						component: HelpComponent
					},

					{
						path: 'notifications',
						title: `${TitlePrefix} - Notitications`,
						component: NotificationsComponent
					},

					{
						path: 'invite',
						title: `${TitlePrefix} - Account Invitations`,
						component: AccountInviteComponent
					},

					/*
					 * --------------------------------------------------------
					 * SETUP / ADMINISTRATION
					 * --------------------------------------------------------
					 */
					{
						path: 'setup',
						title: `${TitlePrefix} - Setup`,
						// component: RouteFolderComponent,

						children: [

							{
								path: 'data-model-management/:organisation',
								title: `${TitlePrefix} - Data Model Management`,
								component: OrganisationDataModelComponent,
								canActivate: [AccountAdminGuard]
							},


							{
								path: 'hierarchy-management/:organisation',
								title: `${TitlePrefix} - Hierarchy Management`,
								component: HierarchyManagementComponent,
								canActivate: [AccountAdminGuard]
							},

							{
								path: 'integration-management/:organisation',
								title: `${TitlePrefix} - Data Management`,
								component: DataIntegrationsComponent,
								canActivate: [AccountAdminGuard],

								children: [
									{
										path: 'integration/:integrationID',
										component: DataIntegrationsDetailComponent
									}
								]

							},

							{
								path: 'integration-management/:organisation/integration/:integrationID/neuronsensors',
								title: `${TitlePrefix} - Data Management`,
								component: DataIntegrationsNeuronsensorsManageComponent,
								canActivate: [AccountAdminGuard]
							},

							{
								path: 'integration-management/:organisation/integration/:integrationID/virtualsensors',
								title: `${TitlePrefix} - Data Management`,
								component: DataIntegrationsVirtualsensorsManageComponent,
								canActivate: [AccountAdminGuard]
							},

							{
								path: 'dashboard-management/:organisation',
								title: `${TitlePrefix} - Dahsboard Management`,
								component: DashboardCreationComponent,
								canActivate: [FeatureGuard, AccountAdminGuard],
								data: {
									featureID: AccountFeatures.Custom_Dashboards
								}
							},

							{
								path: 'virtual-datapoint-management/:organisation',
								title: `${TitlePrefix} - Virtual Datapoints Management`,
								component: VirtualSensorsComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Virtual_Sensors
								}
							},

							{
								path: 'virtual-datapoint-management/:organisation/virtual-datapoint/:virtualSensorID',
								title: `${TitlePrefix} - Virtual Datapoints Management`,
								component: VirtualSensorDetailsComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Virtual_Sensors
								}
							}
						]
					},

					{
						path: 'routing',
						title: `${TitlePrefix} - Re Routing`,
						component: RoutingComponent
					},


					/*
					 * --------------------------------------------------------
					 * LEGACY ORGANISATION ROUTES
					 * --------------------------------------------------------
					 *
					 * These redirect old URLs to the new routing structure.
					 * Keep these before the dynamic organisation routes.
					 */
					{
						path: 'organisations/:organisation/dashboard',
						title: `${TitlePrefix} - Re Routing`,
						component: RedirectComponent
					},

					{
						path: 'organisations/:organisation/location/:sublocation/asset/:assetID',
						title: `${TitlePrefix} - Re Routing`,
						component: RedirectComponent
					},

					{
						path: 'organisations/:organisation/location/:sublocation',
						title: `${TitlePrefix} - Re Routing`,
						component: RedirectComponent
					},

					{
						path: 'organisations/:organisation/asset/:assetID',
						title: `${TitlePrefix} - Re Routing`,
						component: RedirectComponent
					},

					{
						path: 'organisations/:organisation',
						title: `${TitlePrefix} - Re Routing`,
						component: RedirectComponent
					},


					/*
					 * ========================================================
					 * ACCOUNT/ORGANISATION ROUTES
					 * ========================================================
					 *
					 * All account/organisation-specific routes share:
					 *
					 *     :account/organisation/:organisation
					 *
					 * CheckActiveRoute is therefore applied once to the
					 * organisation route group and protects every child.
					 */
					{
						path: ':account/organisation/:organisation',
						title: `${TitlePrefix} - Account Organisation`,
						canActivateChild: [AccountOrganisationGuard],
						children: [

							/*
							 * ------------------------------------------------
							 * ORGANISATION HOME
							 * ------------------------------------------------
							 */
							{
								path: '',
								title: `${TitlePrefix} - Account Organisation`,
								component: HomeDefaultPageComponent
							},

							{
								path: 'dashboard',
								title: `${TitlePrefix} - Dashboard`,
								component: CustomDashboardComponent
							},


							/*
							 * ------------------------------------------------
							 * EVENTS
							 * ------------------------------------------------
							 */
							{
								path: 'event',
								title: `${TitlePrefix} - Events`,
								component: EventsComponent
							},

							{
								path: 'event/:eventID',
								title: `${TitlePrefix} - Event Details`,
								component: EventDetailsComponent
							},


							/*
							 * ------------------------------------------------
							 * RULES
							 * ------------------------------------------------
							 */
							{
								path: 'rules',
								title: `${TitlePrefix} - Rules`,
								component: RulesComponent
							},

							{
								path: 'rules/:ruleID',
								title: `${TitlePrefix} - Rule Details`,
								component: RuleDetailsComponent
							},


							/*
							 * ------------------------------------------------
							 * ACTIONS
							 * ------------------------------------------------
							 */
							{
								path: 'actions',
								title: `${TitlePrefix} - Actions`,
								component: ActionListComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Actions
								}
							},

							{
								path: 'actions/:actionID',
								title: `${TitlePrefix} - Action Details`,
								component: ActionDetailsComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Actions
								}
							},


							/*
							 * ------------------------------------------------
							 * WATCH LIST
							 * ------------------------------------------------
							 */
							{
								path: 'watch',
								title: `${TitlePrefix} - Watch List`,
								component: AssetListComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Watch_List
								},

								children: [
									{
										path: ':assetID',
										title: `${TitlePrefix} - Watch List Asset`,
										component: WatchListComponent,
										canActivate: [FeatureGuard],
										data: {
											featureID: AccountFeatures.Watch_List
										}
									}
								]
							},


							/*
							 * ------------------------------------------------
							 * MODELS
							 * ------------------------------------------------
							 */
							{
								path: 'models',
								title: `${TitlePrefix} - Models`,
								component: ModelsComponent
							},

							{
								path: 'models/:modelID',
								title: `${TitlePrefix} - Model Details`,
								component: ModelDetailsComponent
							},


							/*
							 * ------------------------------------------------
							 * FAVOURITES
							 * ------------------------------------------------
							 */
							{
								path: 'favourites',
								title: `${TitlePrefix} - Favorites`,
								component: UserFavouritesComponent
							},

							/*
							 * ------------------------------------------------
							 * SAP / CMMS
							 * ------------------------------------------------
							 */
							{
								path: 'SAPCases',
								title: `${TitlePrefix} - SAP Cases`,
								component: SAPCaseListComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.SAP
								}
							},

							{
								path: 'SAPCases/:caseID',
								title: `${TitlePrefix} - SAP Case Details`,
								component: SAPCaseDetailComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.SAP
								}
							},


							/*
							 * ------------------------------------------------
							 * FLAGGED ASSETS
							 * ------------------------------------------------
							 */
							{
								path: 'flagged',
								title: `${TitlePrefix} - Flagged Assets`,
								component: OrganisationFlaggedAssetsComponent
							},


							/*
							 * ------------------------------------------------
							 * ENERGY
							 * ------------------------------------------------
							 */
							{
								path: 'energy-dashboard',
								title: `${TitlePrefix} - Energy Dashboard`,
								component: EnergyDashboardComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Energy_Monitoring
								}
							},

							{
								path: 'utility-reporting',
								title: `${TitlePrefix} - Utility Reporting`,
								component: EnergyReportingComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Energy_Monitoring
								}
							},

							{
								path: 'utility-report-history',
								title: `${TitlePrefix} - Utility History Reporting`,
								component: HistoricalReportsComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Energy_Monitoring
								}
							},


							/*
							 * ------------------------------------------------
							 * ASSETS
							 * ------------------------------------------------
							 */
							{
								path: 'assets',
								title: `${TitlePrefix} - Assets`,
								component: AssetListComponent
							},

							{
								path: 'asset/:assetID',
								title: `${TitlePrefix} - Asset Details`,
								component: AssetDetailsComponent
							},

							{
								path: 'compare',
								title: `${TitlePrefix} - Compare Assets`,
								component: AssetComparisonComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Compare_Assets
								}
							},


							/*
							 * ------------------------------------------------
							 * LOCATIONS
							 * ------------------------------------------------
							 */
							{
								path: 'location/:sublocation',
								title: `${TitlePrefix} - Functional Locations`,
								component: HierarchyDashboardComponent,
								children: [
									{
										path: 'asset/:assetID',
										title: `${TitlePrefix} - Functional Locations`,
										component: AssetDetailsComponent
									}
								]
							}
						]
					}
				]
			}
		]
	},



	/*
	 * ============================================================
	 * NON-AUTHENTICATED / SYSTEM ROUTES
	 * ============================================================
	 */
	{
		path: 'accountdisabled',
		component: AccountdisabledComponent
	},

	{
		path: 'unverified',
		component: UnverifiedEmailComponent
	},

	{
		path: 'unauthorised',
		component: UnauthorisedComponent
	},

	{
		path: 'error',
		component: ErrorComponent
	},

	{
		path: 'login',
		component: LoginComponent
	},

	{
		path: 'logout',
		component: LogoutComponent
	}
];


@NgModule({
	imports: [
		RouterModule.forRoot(routes, {
			scrollPositionRestoration: 'disabled'
		})
	],
	exports: [
		RouterModule
	]
})
export class AppRoutingModule { }